import React, { memo } from 'react'
import { useFieldArray } from 'react-hook-form'
import Grid from 'transfix-ui/core/Grid'
import { IShipmentStopTypes } from 'types/graphqlTypes'
import { useCreateShipmentFormContext } from '../../../context'
import { TStopSchema } from '../../../schema/ShipmentSchema/StopSchema'
import StopFormSection from '../StopFormSection'
import useFirstPickupValues from './hooks/useFirstPickupValues'

export interface IShipmentStopsFormSectionProps {
  /**
   * @example '124212'
   */
  shipperApplicationId: string
}

const ShipmentStopsFormSection = memo<IShipmentStopsFormSectionProps>(
  ({ shipperApplicationId }) => {
    /**
     * Form Context
     */
    const { control, setValue } = useCreateShipmentFormContext()

    /**
     * Stops Field Array
     */
    const { append, fields: stops, insert, remove } = useFieldArray<TStopSchema>({
      control,
      name: 'shipment.stops',
    })
    const pickupStops = stops.filter(({ stopType }) => stopType === IShipmentStopTypes.pickup)
    const deliveryStops = stops.filter(({ stopType }) => stopType === IShipmentStopTypes.delivery)

    /**
     * Inserts a pickup stop after the last pickup
     */
    const addPickupHandler = () => {
      const numOfPickups = pickupStops.length
      insert(numOfPickups, {
        stopType: IShipmentStopTypes.pickup,
        stopSequence: numOfPickups + 1,
      })
    }

    /**
     * Appends a delivery stop to the end of the stops
     */
    const addDeliveryHandler = () => {
      const numOfDeliveries = deliveryStops.length
      append({
        stopType: IShipmentStopTypes.delivery,
        stopSequence: numOfDeliveries + 1,
      })
    }

    /**
     * Removes a stop at a specific index
     */
    const deleteStopHandler = (index: number) => {
      remove(index)
    }

    /**
     * This hook tracks changes made to the first pickup
     * and copies values over to other stops and shipment terms
     */
    useFirstPickupValues({ control, setValue, numberOfStops: stops.length })

    return (
      <Grid container spacing={5}>
        <Grid item md={6} data-test='pickup-stops'>
          {pickupStops.map(({ dropTrailer, id }, index) => {
            const isLastPickup = index + 1 === pickupStops.length
            const includeDeleteButton = pickupStops.length > 1
            return (
              <StopFormSection
                key={id}
                stopIndex={index}
                stopType={IShipmentStopTypes.pickup}
                stopSequence={index + 1}
                shipperApplicationId={shipperApplicationId}
                dropTrailer={dropTrailer}
                includeAddButton={isLastPickup}
                includeDeleteButton={includeDeleteButton}
                onAddStop={addPickupHandler}
                onDeleteStop={deleteStopHandler}
                stopAppointmentType='pickup'
              />
            )
          })}
        </Grid>
        <Grid item md={6} data-test='delivery-stops'>
          {deliveryStops.map(({ dropTrailer, id }, index) => {
            const offset = pickupStops.length
            const isLastDelivery = index + 1 === deliveryStops.length
            const includeDeleteButton = deliveryStops.length > 1
            return (
              <StopFormSection
                key={id}
                /** Delivery stopIndex is offset by number of pickups */
                stopIndex={index + offset}
                stopType={IShipmentStopTypes.delivery}
                stopSequence={index + 1}
                shipperApplicationId={shipperApplicationId}
                dropTrailer={dropTrailer}
                includeAddButton={isLastDelivery}
                includeDeleteButton={includeDeleteButton}
                onAddStop={addDeliveryHandler}
                onDeleteStop={deleteStopHandler}
                stopAppointmentType='delivery'
              />
            )
          })}
        </Grid>
      </Grid>
    )
  }
)

export default ShipmentStopsFormSection
